import { Controller } from "stimulus";

export default class extends Controller {
  static targets = ["billingYearInput", "fileInput", "statusMessage", "submitButton", "urlInput"];

  connect() {
    this.importInProgress = false;
    this.googleScriptPromise = null;
    this.originalSubmitLabel = this.submitButtonTarget.value;
    this.restoreLastUsedUrl();
  }

  async submit(event) {
    this.clearStatusMessage();

    if (!this.billingYearIsValid()) {
      event.preventDefault();
      this.showStatusMessage('Please provide a valid Billing Year.');
      return;
    }

    if (this.fileInputTarget.files.length > 0) {
      this.storeLastUsedUrl();
      return;
    }

    const url = this.urlInputTarget.value.trim();
    if (!url) {
      event.preventDefault();
      this.showStatusMessage('Please choose a spreadsheet file or provide a Google Sheets URL.');
      return;
    }

    event.preventDefault();
    if (this.importInProgress) return;

    this.importInProgress = true;
    this.setSubmitButtonState(true, 'Importing from Google…');
    this.storeLastUsedUrl();

    try {
      const file = await this.fetchSpreadsheetAsXlsx(url);
      this.attachFile(file);
      this.setSubmitButtonState(false, this.originalSubmitLabel);
      this.importInProgress = false;
      if (this.element.requestSubmit) {
        this.element.requestSubmit();
      } else {
        this.element.submit();
      }
    } catch (error) {
      this.setSubmitButtonState(false, this.originalSubmitLabel);
      this.importInProgress = false;
      this.showStatusMessage(error.message || 'Could not import Google Sheet.');
    }
  }

  storeLastUsedUrl() {
    if (!this.hasUrlInputTarget) return;

    const key = this.lastUsedUrlStorageKey();
    const value = this.urlInputTarget.value.trim();

    if (value) {
      localStorage.setItem(key, value);
    } else {
      localStorage.removeItem(key);
    }
  }

  restoreLastUsedUrl() {
    if (!this.hasUrlInputTarget) return;
    if (this.urlInputTarget.value.trim().length > 0) return;

    const value = localStorage.getItem(this.lastUsedUrlStorageKey());
    if (value) this.urlInputTarget.value = value;
  }

  lastUsedUrlStorageKey() {
    const userId = this.data.get('userId') || 'unknown-user';
    return `billing-recon-last-google-sheet-url-${userId}`;
  }

  googleTokenStorageKey() {
    const userId = this.data.get('userId') || 'unknown-user';
    return `billing-recon-google-access-token-${userId}`;
  }

  billingYearIsValid() {
    const value = this.billingYearInputTarget.value.toString().trim();
    return /^\d{4}$/.test(value) && Number(value) >= 2000 && Number(value) <= 2100;
  }

  setSubmitButtonState(disabled, text) {
    this.submitButtonTarget.disabled = disabled;
    this.submitButtonTarget.value = text;
  }

  clearStatusMessage() {
    if (!this.hasStatusMessageTarget) return;

    this.statusMessageTarget.className = 'ui hidden message';
    this.statusMessageTarget.textContent = '';
  }

  showStatusMessage(text) {
    if (!this.hasStatusMessageTarget) return;

    this.statusMessageTarget.className = 'ui negative message';
    this.statusMessageTarget.textContent = text;
  }

  async fetchSpreadsheetAsXlsx(url) {
    const googleClientId = (this.data.get('googleClientId') || '').toString().trim();
    if (!googleClientId) {
      throw new Error('Google URL import is not configured (missing client ID).');
    }

    const fileId = this.extractGoogleFileId(url);
    if (!fileId) {
      throw new Error('Please provide a valid Google Sheets URL.');
    }

    let accessToken = await this.requestGoogleAccessToken(googleClientId);
    let response = await this.exportSpreadsheet(fileId, accessToken);
    if (response.status === 401) {
      this.clearCachedAccessToken();
      accessToken = await this.requestGoogleAccessToken(googleClientId);
      response = await this.exportSpreadsheet(fileId, accessToken);
    }

    if (!response.ok) {
      if (response.status === 403 || response.status === 404) {
        throw new Error("Couldn't access this sheet. Make sure it is shared with your Google account.");
      }

      throw new Error('Google export failed. Please try again or use manual upload.');
    }

    const blob = await response.blob();
    if (!blob || blob.size === 0) {
      throw new Error('Google export returned an empty file.');
    }

    const billingYear = this.billingYearInputTarget.value.toString().trim();
    const filename = `billing-recon-${billingYear}.xlsx`;
    return new File([blob], filename, {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
  }

  exportSpreadsheet(fileId, accessToken) {
    return fetch(
      `https://www.googleapis.com/drive/v3/files/${encodeURIComponent(fileId)}/export?mimeType=application/vnd.openxmlformats-officedocument.spreadsheetml.sheet`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      }
    );
  }

  extractGoogleFileId(urlValue) {
    let parsedUrl;
    try {
      parsedUrl = new URL(urlValue);
    } catch (_error) {
      return null;
    }

    if (!['docs.google.com', 'drive.google.com'].includes(parsedUrl.hostname)) {
      return null;
    }

    const spreadsheetMatch = parsedUrl.pathname.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
    if (spreadsheetMatch) return spreadsheetMatch[1];

    const driveFileMatch = parsedUrl.pathname.match(/\/file\/d\/([a-zA-Z0-9-_]+)/);
    if (driveFileMatch) return driveFileMatch[1];

    const queryId = parsedUrl.searchParams.get('id');
    if (queryId && /^[a-zA-Z0-9-_]+$/.test(queryId)) return queryId;

    return null;
  }

  async requestGoogleAccessToken(googleClientId) {
    const cachedAccessToken = this.readCachedAccessToken();
    if (cachedAccessToken) return cachedAccessToken;

    await this.ensureGoogleScriptLoaded();

    try {
      const tokenResponse = await this.requestTokenWithPrompt(googleClientId, '');
      this.cacheAccessToken(tokenResponse);
      return tokenResponse.access_token;
    } catch (error) {
      if (!this.isRecoverableGoogleAuthError(error)) {
        throw error;
      }
    }

    const consentTokenResponse = await this.requestTokenWithPrompt(googleClientId, 'consent');
    this.cacheAccessToken(consentTokenResponse);
    return consentTokenResponse.access_token;
  }

  requestTokenWithPrompt(googleClientId, promptValue) {
    return new Promise((resolve, reject) => {
      const tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: googleClientId,
        scope: 'https://www.googleapis.com/auth/drive.readonly',
        callback: (tokenResponse) => {
          if (!tokenResponse || tokenResponse.error || !tokenResponse.access_token) {
            reject(this.googleAuthError(tokenResponse));
            return;
          }

          resolve(tokenResponse);
        },
        error_callback: () => {
          reject(this.googleAuthError());
        }
      });

      tokenClient.requestAccessToken({ prompt: promptValue });
    });
  }

  googleAuthError(tokenResponse = null) {
    const code = tokenResponse && tokenResponse.error ? tokenResponse.error : 'auth_failed';
    const message = this.googleAuthErrorMessage(code);
    const error = new Error(message);
    error.code = code;
    return error;
  }

  googleAuthErrorMessage(code) {
    if (['popup_closed', 'access_denied', 'user_cancelled', 'interaction_required'].includes(code)) {
      return 'Google authorization was cancelled or failed.';
    }

    return 'Google authorization failed.';
  }

  isRecoverableGoogleAuthError(error) {
    return ['consent_required', 'interaction_required', 'login_required'].includes(error.code);
  }

  readCachedAccessToken() {
    try {
      const raw = sessionStorage.getItem(this.googleTokenStorageKey());
      if (!raw) return null;

      const parsed = JSON.parse(raw);
      const token = parsed.accessToken;
      const expiresAt = Number(parsed.expiresAt || 0);
      if (!token || !expiresAt) return null;

      const bufferMs = 30 * 1000;
      if (Date.now() >= expiresAt - bufferMs) {
        this.clearCachedAccessToken();
        return null;
      }

      return token;
    } catch (_error) {
      this.clearCachedAccessToken();
      return null;
    }
  }

  cacheAccessToken(tokenResponse) {
    const accessToken = tokenResponse.access_token;
    if (!accessToken) return;

    const expiresInSeconds = Number(tokenResponse.expires_in || 3600);
    const expiresAt = Date.now() + (expiresInSeconds * 1000);

    sessionStorage.setItem(
      this.googleTokenStorageKey(),
      JSON.stringify({ accessToken, expiresAt })
    );
  }

  clearCachedAccessToken() {
    sessionStorage.removeItem(this.googleTokenStorageKey());
  }

  async ensureGoogleScriptLoaded() {
    if (window.google && window.google.accounts && window.google.accounts.oauth2) return;
    if (this.googleScriptPromise) return this.googleScriptPromise;

    this.googleScriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Could not load Google authentication script.'));
      document.head.appendChild(script);
    });

    return this.googleScriptPromise;
  }

  attachFile(file) {
    const dataTransfer = new DataTransfer();
    dataTransfer.items.add(file);
    this.fileInputTarget.files = dataTransfer.files;
  }
}
